import {
  AccountSchema,
  AuthSessionSchema,
  RegistrationRequestResultSchema,
  RegistrationStatusResultSchema,
  UpdateProfileResultSchema,
  type Account,
  type AuthSession,
  type RegistrationRequestResult,
  type RegistrationStatusResult,
  type UpdateProfileResult,
} from '@lilius/contracts/auth';
import { OkResponseSchema } from '@lilius/contracts/api';
import { ApiError, apiRequest } from '@/shared/api/client';
import { getInstallationId } from '@/shared/installation';

export type SessionView = AuthSession;

export const authApi = {
  login: (phone: string, password: string) =>
    apiRequest<AuthSession>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ phone, password, installationId: getInstallationId() }),
      schema: AuthSessionSchema,
    }),

  register: (phone: string, email: string, password: string) =>
    apiRequest<RegistrationRequestResult>('/auth/register', {
      method: 'POST',
      body: JSON.stringify({ phone, email, password }),
      schema: RegistrationRequestResultSchema,
    }),

  requestPasswordReset: (phone: string, newPassword: string, email?: string) =>
    apiRequest<RegistrationRequestResult>('/auth/password-reset-requests', {
      method: 'POST',
      body: JSON.stringify({ phone, newPassword, ...(email ? { email } : {}) }),
      schema: RegistrationRequestResultSchema,
    }),

  resendRegistrationOtp: (requestId: string) =>
    apiRequest<{ ok: true }>(`/auth/registrations/${requestId}/resend-otp`, {
      method: 'POST',
      body: JSON.stringify({}),
      schema: OkResponseSchema,
    }),

  registrationStatus: (requestId: string) =>
    apiRequest<RegistrationStatusResult>(`/auth/registrations/${requestId}/status`, {
      schema: RegistrationStatusResultSchema,
    }),

  verifyRegistration: (requestId: string, code: string) =>
    apiRequest<AuthSession>(`/auth/registrations/${requestId}/verify`, {
      method: 'POST',
      body: JSON.stringify({ code, installationId: getInstallationId() }),
      schema: AuthSessionSchema,
    }),

  requestEmailOtp: (email: string) =>
    apiRequest<{ ok: true; email: string }>('/auth/me/email/request-otp', {
      method: 'POST',
      body: JSON.stringify({ email }),
    }),

  verifyEmailOtp: (email: string, code: string) =>
    apiRequest<{ account: Account }>('/auth/me/email/verify-otp', {
      method: 'POST',
      body: JSON.stringify({ email, code }),
    }),

  async me(): Promise<SessionView | null> {
    try {
      return await apiRequest<AuthSession>('/auth/me', { schema: AuthSessionSchema });
    } catch (error) {
      if (error instanceof ApiError && error.status === 401) return null;
      throw error;
    }
  },

  updateProfile: (displayName: string) =>
    apiRequest<UpdateProfileResult>('/auth/me', {
      method: 'PATCH',
      body: JSON.stringify({ displayName }),
      schema: UpdateProfileResultSchema,
    }),

  changePassword: (currentPassword: string, newPassword: string) =>
    apiRequest('/auth/me/password', {
      method: 'PATCH',
      body: JSON.stringify({ currentPassword, newPassword }),
      schema: OkResponseSchema,
    }),

  logout: () => apiRequest('/auth/logout', { method: 'POST', schema: OkResponseSchema }),
};
